import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SelectAllIcon from '@mui/icons-material/DoneAll';
import { Box, Button, Checkbox, ClickAwayListener, FormControl, FormControlLabel, InputLabel, Grid, Paper, Popper, Select, Toolbar, Typography, useMediaQuery, useTheme } from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { makeStyles } from '@mui/styles';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import { TimePicker } from '../../components/TimePicker';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React from 'react';
import PropTypes from 'prop-types';

import ExportMenuButton from '../../components/ExportMenuButton';
import { ContextStore } from '../../store/contextStore';
import { useIsWidthUp } from '../../utils';
import TripUserListRemoteAutocomplete from './TripUserListRemoteAutocomplete';
import { bookingStatesMenuItems } from './utils';
import RemoteAutocomplete from './AddUserRemoteAutoComplete';

const useToolbarStyles = makeStyles((theme) => ({
    toolbarRoot: {
        marginBottom: 0,
        paddingTop: '0.5rem',
        padding: '1rem',
        paddingBottom: '1rem',
        width: '100%',
        [theme.breakpoints.down('sm')]: {
            height: 'auto',
            padding: '1rem',
        },
    },
    hidden: {
        visibility: 'hidden',
    },
    grow: {
        flexGrow: 1,
    },
    filterListContainer: {
        display: 'flex',
        flexShrink: 0,
        overflowX: 'hidden',
        gap: '0.25rem',
    },
    filterCapsule: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '0.5rem',
        padding: '6px 16px',
        overflow: 'hidden',
        borderRadius: '16px',
        backgroundColor: '#E0E0E0',
        color: 'rgba(0, 0, 0, 0.87)',
        height: 'auto',
        flexShrink: 0,
        maxWidth: '200px',
        margin: '0.25rem',
    },
    filtersPaperModalContainer: {
        boxShadow: '0px 8px 10px rgba(0, 0, 0, 0.14), 0px 3px 14px rgba(0, 0, 0, 0.12), 0px 5px 5px rgba(0, 0, 0, 0.2)',
        borderRadius: '4px',
        backgroundColor: 'white',
        width: '350px',
        height: '250px',
        padding: '1rem',
        marginBottom: '1rem',
        zIndex: 100000,
    },
}));

export const UserListToolbar = (props) => {
    const createBt = React.useRef(null);
    const classes = useToolbarStyles();
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const [searchFiltersAnchorEl, setSearchFiltersAnchorEl] = React.useState(null);
    const { hasPerm, accessGroupsEnabled } = React.useContext(ContextStore);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const isSmallScreen = !useIsWidthUp('sm');
    const isWideDesktop = useMediaQuery('(min-width: 1730px)');
    const isMediumDesktop = useMediaQuery('(min-width: 1320px)');

    const dateRangePickerKey = `dr-${props.recordListFilters.dateStartFilter || ''}-${props.recordListFilters.dateEndFilter || ''}`;
    const dateRangePickerDefaultValue = React.useMemo(() => [
        props.recordListFilters.dateStartFilter ? moment(props.recordListFilters.dateStartFilter) : null,
        props.recordListFilters.dateEndFilter ? moment(props.recordListFilters.dateEndFilter) : null,
    ], [props.recordListFilters.dateStartFilter, props.recordListFilters.dateEndFilter]);

    const handleDateAccept = (acceptedDateRangeValue) => {
        const [newStart, newEnd] = acceptedDateRangeValue || [null, null];

        const newStartDateString = newStart ? moment(newStart).format('YYYY-MM-DD') : null;
        const newEndDateString = newEnd ? moment(newEnd).format('YYYY-MM-DD') : null;

        if (newStartDateString !== props.recordListFilters.dateStartFilter || newEndDateString !== props.recordListFilters.dateEndFilter) {
            props.setRecordListFilters(prev => ({
                ...prev,
                dateStartFilter: newStartDateString,
                dateEndFilter: newEndDateString,
            }));
            props.setLastUpdate(new Date().toString());
        }
    };

    const onAccessGroupsChange = (ev, nv) => {
        props.setRecordListFilters && props.setRecordListFilters((prev) => ({
            ...prev,
            agroups: (nv || []).map(x => ({ id: x.id, name: x.name }))
        }));
    }

    const handleRoleFilterSelect = (ev, role) => {
        if (role) {
            if (props.recordListFilters.roleFilter) {
                let roleFilterAlreadyExisted = [...props.recordListFilters.roleFilter?.filter((r) => r === role.id)];
                if (roleFilterAlreadyExisted.length > 0) {
                    props.setRecordListFilters((prevState) => {
                        if (prevState.roleFilter) {
                            return { ...prevState, roleFilter: [...prevState.roleFilter.filter((r) => r !== role.id)] };
                        }
                    });
                    removeSearchFilter({ id: role.id, name: role.name, type: 'role' });
                } else {
                    props.setRecordListFilters((prevState) => ({ ...prevState, roleFilter: [role.id] }));
                    props.setSearchFilterList((prevState) => {
                        let existingSearchFilters = prevState.filter((f) => f.type !== 'role');
                        return [...existingSearchFilters, { id: role.id, name: role.name, type: 'role' }];
                    });
                }
            } else {
                props.setRecordListFilters((prevState) => ({ ...prevState, roleFilter: [role.id] }));
                handleNewSearchFilter({ id: role.id, name: role.name, type: 'role' });
            }
        } else {
            props.setRecordListFilters((prevState) => ({ ...prevState, roleFilter: null }));
            props.setSearchFilterList((prevState) => {
                let existingSearchFilters = prevState.filter((f) => f.type !== 'role');
                return [...existingSearchFilters];
            });
        }
    };

    const handleNewSearchFilter = (nf) => {
        if (nf) {
            props.setSearchFilterList((prevState) => {
                let existingFilters = prevState.filter((f) => f.id === nf.id);
                if (existingFilters.length > 0) {
                    enqueueSnackbar('Filter is already in use', { variant: 'error' });
                    return [...prevState];
                }
                return [...prevState, nf];
            });
        }
    };
    // Flip a tag filter between "include" (has this tag) and "exclude" (NOT this tag).
    const toggleExcludeSearchFilter = (nf) => {
        if (nf.type !== 'tag') return;
        props.setSearchFilterList((prevState) =>
            prevState.map((f) => (f.id === nf.id ? { ...f, exclude: !f.exclude } : f))
        );
    };
    const removeSearchFilter = (nf) => {
        props.setSearchFilterList((prevState) => {
            let existingFilters = prevState.filter((f) => f.id !== nf.id);
            if (existingFilters.length === 0) {
                setSearchFiltersAnchorEl(null);
            }
            return [...existingFilters];
        });

        if (nf.type === 'role') {
            props.setRecordListFilters((prevState) => {
                let removed = [];
                if (prevState.roleFilter) {
                    removed = prevState.roleFilter.filter((rf) => rf !== nf.id);
                }

                if (removed.length === 0) {
                    return { ...prevState, roleFilter: null };
                } else {
                    return { ...prevState, roleFilter: [...removed] };
                }
            });
        }
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const tripAssociatedTagsIds = props.trip.tag_categories.map((cat) => {
        return cat.category.id;
    });

    const onSelectChange = (ev) => {
        props.setRecordListFilters((prevState) => ({
            ...prevState,
            [ev.target.name]: ev.target.value
        }));
    };

    const handleGroupingToggle = (ev) => {
        props.setRecordListFilters((prevState) => ({
            ...prevState,
            groupByMainBooking: ev.target.checked,
        }));
        props.setLastUpdate(new Date().toString());
    };

    const getDeparturePickerValue = (value) => (value ? moment(value, 'HH:mm') : null);

    const handleDepartureTimeChange = (fieldName) => (newValue, context) => {
        if (context?.validationError) {
            return;
        }

        const nextValue = newValue ? moment(newValue).format('HH:mm') : null;
        if ((props.recordListFilters[fieldName] || null) === nextValue) {
            return;
        }

        props.setRecordListFilters((prevState) => ({
            ...prevState,
            [fieldName]: nextValue,
        }));
        props.setLastUpdate(new Date().toString());
    };

    // Same menu as the desktop header (CP-8289). On a phone the header is
    // hidden, so this is the only Export on the Users tab (CP-8379).
    const exportActions = [
        hasPerm('tour_participant', 'read') && props.onExportList && {
            key: 'csv',
            label: 'Export as CSV',
            icon: <DescriptionOutlinedIcon fontSize="small" />,
            onClick: props.onExportList,
        },
        hasPerm(props.resource, 'export') && props.onDocumentExport && {
            key: 'xml',
            label: 'Export as XML (import format)…',
            icon: <DescriptionOutlinedIcon fontSize="small" />,
            onClick: () => props.onDocumentExport('xml'),
        },
        hasPerm(props.resource, 'export') && props.onDocumentExport && {
            key: 'json',
            label: 'Export as JSON (import format)…',
            icon: <DescriptionOutlinedIcon fontSize="small" />,
            onClick: () => props.onDocumentExport('json'),
        },
    ];
    const showMobileListActions = isSmallScreen && !props.tourUserId;
    const showMobileAdd = showMobileListActions && hasPerm('tour_participant', 'write');
    const showMobileExport = showMobileListActions && exportActions.some(Boolean);

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            {!hasPerm('tour_participant', 'write') || isSmallScreen || props.tourUserId ? null : (
                <Box style={{ position: 'fixed', top: '0.65rem', right: 20, display: 'flex', gap: '0.5rem', zIndex: 1200 }}>
                    {props.onSelectAll && (
                        <Button
                            disabled={props.syncActive}
                            color="primary"
                            variant="outlined"
                            sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: '#F5FAFF' } }}
                            onClick={props.selectedIds?.length > 0 ? props.onDeselectAll : props.onSelectAll}
                        >
                            {props.selectedIds?.length > 0 ? <CloseIcon /> : <SelectAllIcon />}
                            &nbsp;{!isSmallScreen ? (props.selectedIds?.length > 0 ? 'Deselect all' : 'Select all') : null}
                        </Button>
                    )}
                    {props.selectedIds?.length > 0 && (
                        <Button
                            id="basic-button"
                            aria-controls={open ? 'basic-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? 'true' : undefined}
                            onClick={handleClick}
                            color="primary"
                            variant="contained"
                            disabled={props.syncActive}
                        >
                            <MenuIcon />
                            &nbsp; Actions
                        </Button>
                    )}
                    {props.selectedIds?.length === 0 && (
                        <>
                            <Button
                                ref={createBt}
                                disabled={props.syncActive}
                                color="primary"
                                variant="contained"
                                onClick={props.showUserAddForm}
                                className={props.id ? classes.hidden : null}
                            >
                                <AddIcon />
                                &nbsp;{!isSmallScreen ? 'Add user' : null}
                            </Button>
                            {/* One menu, like Users and Itineraries (CP-8289). */}
                            <ExportMenuButton
                                size="medium"
                                disabled={props.syncActive}
                                actions={exportActions}
                            />
                        </>
                    )}
                    <Menu
                        id="basic-menu"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        MenuListProps={{
                            'aria-labelledby': 'basic-button',
                        }}
                    >
                        {/* <MenuItem
                            onClick={() => {
                                props.deleteUsers();
                                setAnchorEl(null);
                            }}
                        >
                            Remove users
                        </MenuItem> */}
                        {tripAssociatedTagsIds.length > 0 && (
                            <MenuItem
                                onClick={() => {
                                    props.addTags();
                                    setAnchorEl(null);
                                }}
                            >
                                Add tags
                            </MenuItem>
                        )}
                        {tripAssociatedTagsIds.length > 0 && props.nonRequiredTripAssociatedTagsIds.length > 0 && (
                            <MenuItem
                                onClick={() => {
                                    props.removeTags();
                                    setAnchorEl(null);
                                }}
                            >
                                Remove tags
                            </MenuItem>
                        )}
                        <MenuItem
                            onClick={() => {
                                props.addObservedUsers();
                                setAnchorEl(null);
                            }}
                        >
                            Add observed users
                        </MenuItem>
                        {/* <MenuItem
                            onClick={() => {
                                props.removeObservedUsers();
                                setAnchorEl(null);
                            }}
                        >
                            Remove observed users
                        </MenuItem> */}
                        {hasPerm('ops', 'change_booking_user') && props.selectedIds?.length > 0 && (
                            <MenuItem
                                onClick={() => {
                                    props.openBatchChangeOwner && props.openBatchChangeOwner();
                                    setAnchorEl(null);
                                }}
                            >
                                Change owner…
                            </MenuItem>
                        )}
                        {hasPerm(props.resource, 'export') && props.onDocumentExport && (
                            <MenuItem
                                onClick={() => {
                                    props.onDocumentExport();
                                    setAnchorEl(null);
                                }}
                            >
                                Export as XML / JSON…
                            </MenuItem>
                        )}
                    </Menu>
                </Box>
            )}
            <Popper open={Boolean(searchFiltersAnchorEl)} style={{ zIndex: 1300 }} anchorEl={searchFiltersAnchorEl} onClose={() => setSearchFiltersAnchorEl(null)} placement="bottom-start" disablePortal={false}>
                <ClickAwayListener onClickAway={() => setSearchFiltersAnchorEl(null)}>
                    <Paper>
                        <div className={classes.filtersPaperModalContainer}>
                            <div style={{ height: '200px', overflowY: 'auto' }}>
                                {props.searchFilterList.map((filter) => {
                                    return (
                                        <div
                                            key={filter.id}
                                            className={classes.filterCapsule}
                                            style={filter.exclude ? { backgroundColor: '#FDDCDC', color: '#B71C1C' } : undefined}
                                        >
                                            <span
                                                onClick={filter.type === 'tag' ? () => toggleExcludeSearchFilter(filter) : undefined}
                                                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, cursor: filter.type === 'tag' ? 'pointer' : 'default' }}
                                            >
                                                {filter.exclude ? '≠ ' : ''}{filter.name}
                                            </span>
                                            <span style={{ fontSize: '70%', cursor: 'pointer', flexShrink: 0 }} onClick={() => removeSearchFilter(filter)}>
                                                X
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', backgroundColor: 'white', height: '65px', alignItems: 'center' }}>
                                <Button color="primary" variant="outlined" onClick={() => setSearchFiltersAnchorEl(null)}>
                                    Close
                                </Button>
                            </div>
                        </div>
                    </Paper>
                </ClickAwayListener>
            </Popper>
            <Toolbar disableGutters={true} className={classes.toolbarRoot}>
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 2, width: '100%' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'flex-start',
                                gap: 2,
                                flex: '1 1 auto',
                                minWidth: 0,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    flex: { xs: '1 1 100%', sm: '1 1 200px' },
                                    minWidth: { xs: '100%', sm: 200 },
                                    maxWidth: { xs: '100%', sm: 540 },
                                }}
                            >
                                <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                                    <TripUserListRemoteAutocomplete
                                        resource={`users_and_groups/?tour_id=${props.trip.id}&include_all_users=0&include_tags=1&query=`}
                                        urlSuffix={null}
                                        variant="filled"
                                        label="Search by name, tags..."
                                        size="small"
                                        onChange={(ev, nt) => handleNewSearchFilter(nt)}
                                        setLastUpdate={props.setLastUpdate}
                                        isSmallScreen={isSmallScreen}
                                        searchFilterList={props.searchFilterList}
                                        setRecordListFilters={props.setRecordListFilters}
                                        recordListFilters={props.recordListFilters}
                                        setRecordListQuery={props.setRecordListQuery}
                                        showSearchFilters={setSearchFiltersAnchorEl}
                                        trip={props.trip}
                                    />
                                </Box>
                                {/* Phone: [+] and Export at the end of the search row, as on
                                    Activities and Itineraries. The [+] used to be a FAB over
                                    the departure-time fields (CP-8379). */}
                                {showMobileAdd || showMobileExport ? (
                                    <Box sx={{ display: 'flex', gap: 1, flex: '0 0 auto', alignItems: 'center' }}>
                                        {showMobileAdd && (
                                            <Button
                                                disabled={props.syncActive}
                                                color="primary"
                                                variant="contained"
                                                onClick={props.showUserAddForm}
                                                aria-label="Add user"
                                            >
                                                <AddIcon />
                                            </Button>
                                        )}
                                        {showMobileExport && (
                                            <ExportMenuButton
                                                hideLabel
                                                size="medium"
                                                disabled={props.syncActive}
                                                actions={exportActions}
                                            />
                                        )}
                                    </Box>
                                ) : null}
                            </Box>
                            {!props.trip.is_bespoke && isWideDesktop && (
                                <Box
                                    sx={{
                                        flex: { xs: '1 1 100%', sm: '1 1 160px' },
                                        minWidth: { xs: '100%', sm: 160 },
                                        maxWidth: { xs: '100%', sm: 300 },
                                    }}
                                >
                                    <DateRangePicker
                                        key={dateRangePickerKey}
                                        slots={{ field: SingleInputDateRangeField }}
                                        name="allowedRange"
                                        defaultValue={dateRangePickerDefaultValue}
                                        onAccept={handleDateAccept}
                                        format="MMMM D, YYYY"
                                        label={'Travel Date'}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                variant: 'filled',
                                                fullWidth: true,
                                            },
                                            actionBar: {
                                                actions: ['clear', 'accept'],
                                            },
                                        }}
                                    />
                                </Box>
                            )}
                            {isWideDesktop && (
                                <>
                                    <Box sx={{ width: { xs: '100%', sm: 160 }, flex: { xs: '1 1 100%', sm: '0 0 160px' } }}>
                                        <FormControl fullWidth variant="filled" size="small">
                                            <InputLabel id="override_mode">Booking state</InputLabel>
                                            <Select name="bookingState" labelId="bookingState" id="bookingState" value={props.recordListFilters.bookingState} onChange={onSelectChange} >
                                                {bookingStatesMenuItems}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                    <Box sx={{ width: { xs: '100%', sm: 140 }, flex: { xs: '1 1 100%', sm: '0 0 140px' } }}>
                                        <TimePicker
                                            label="Dep. from"
                                            value={getDeparturePickerValue(props.recordListFilters.departureTimeStart)}
                                            onChange={handleDepartureTimeChange('departureTimeStart')}
                                            ampm={true}
                                            slotProps={{
                                                textField: {
                                                    size: 'small',
                                                    variant: 'filled',
                                                    fullWidth: true,
                                                    InputLabelProps: { shrink: true },
                                                },
                                                actionBar: { actions: ['clear'] },
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ width: { xs: '100%', sm: 140 }, flex: { xs: '1 1 100%', sm: '0 0 140px' } }}>
                                        <TimePicker
                                            label="Dep. to"
                                            value={getDeparturePickerValue(props.recordListFilters.departureTimeEnd)}
                                            onChange={handleDepartureTimeChange('departureTimeEnd')}
                                            ampm={true}
                                            slotProps={{
                                                textField: {
                                                    size: 'small',
                                                    variant: 'filled',
                                                    fullWidth: true,
                                                    InputLabelProps: { shrink: true },
                                                },
                                                actionBar: { actions: ['clear'] },
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ width: { xs: '100%', sm: 220 }, flex: { xs: '1 1 100%', sm: '0 0 220px' }, display: 'flex', alignItems: 'center', alignSelf: 'center' }}>
                                        <FormControlLabel
                                            sx={{ m: 0, alignItems: 'center' }}
                                            control={<Checkbox checked={!!props.recordListFilters.groupByMainBooking} onChange={handleGroupingToggle} />}
                                            label="Group by lead booking"
                                        />
                                    </Box>
                                </>
                            )}
                        </Box>

                        {accessGroupsEnabled() && (
                            <Box
                                sx={{
                                    width: { xs: '100%', sm: 'clamp(140px, 18vw, 260px)' },
                                    minWidth: { xs: '100%', sm: 140 },
                                    maxWidth: { xs: '100%', sm: 260 },
                                    flex: '0 0 auto',
                                    ml: { xs: 0, sm: 'auto' },
                                    alignSelf: 'flex-start',
                                }}
                            >
                                <RemoteAutocomplete
                                    multiple
                                    resource="access_group"
                                    urlSuffix={null}
                                    label="Access groups"
                                    variant="filled"
                                    size="small"
                                    fullWidth
                                    onChange={onAccessGroupsChange}
                                    filterSelected
                                    apiParams={{
                                        ordering: 'name',
                                    }}
                                    value={props.recordListFilters.agroups || []}
                                />
                            </Box>
                        )}
                    </Box>

                    {!isWideDesktop && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
                            {!props.trip.is_bespoke && !isMediumDesktop && (
                                <Box
                                    sx={{
                                        flex: { xs: '1 1 100%', sm: '1 1 160px' },
                                        minWidth: { xs: '100%', sm: 160 },
                                        maxWidth: { xs: '100%', sm: 300 },
                                    }}
                                >
                                    <DateRangePicker
                                        key={dateRangePickerKey}
                                        slots={{ field: SingleInputDateRangeField }}
                                        name="allowedRange"
                                        defaultValue={dateRangePickerDefaultValue}
                                        onAccept={handleDateAccept}
                                        format="MMMM D, YYYY"
                                        label={'Travel Date'}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                variant: 'filled',
                                                fullWidth: true,
                                            },
                                            actionBar: {
                                                actions: ['clear', 'accept'],
                                            },
                                        }}
                                    />
                                </Box>
                            )}
                            {isMediumDesktop ? (
                                <Box sx={{ display: 'flex', gap: 2, width: '100%', flexWrap: 'nowrap', alignItems: 'center' }}>
                                    {!props.trip.is_bespoke && (
                                        <Box sx={{ flex: '0 0 300px', minWidth: 300 }}>
                                            <DateRangePicker
                                                key={dateRangePickerKey}
                                                slots={{ field: SingleInputDateRangeField }}
                                                name="allowedRange"
                                                defaultValue={dateRangePickerDefaultValue}
                                                onAccept={handleDateAccept}
                                                format="MMMM D, YYYY"
                                                label={'Travel Date'}
                                                slotProps={{
                                                    textField: {
                                                        size: 'small',
                                                        variant: 'filled',
                                                        fullWidth: true,
                                                    },
                                                    actionBar: {
                                                        actions: ['clear', 'accept'],
                                                    },
                                                }}
                                            />
                                        </Box>
                                    )}
                                    <Box sx={{ flex: '0 0 160px', minWidth: 160 }}>
                                        <FormControl fullWidth variant="filled" size="small">
                                            <InputLabel id="override_mode">Booking state</InputLabel>
                                            <Select name="bookingState" labelId="bookingState" id="bookingState" value={props.recordListFilters.bookingState} onChange={onSelectChange} >
                                                {bookingStatesMenuItems}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                    <Box sx={{ flex: '0 0 140px', minWidth: 140 }}>
                                        <TimePicker
                                            label="Dep. from"
                                            value={getDeparturePickerValue(props.recordListFilters.departureTimeStart)}
                                            onChange={handleDepartureTimeChange('departureTimeStart')}
                                            ampm={true}
                                            slotProps={{
                                                textField: {
                                                    size: 'small',
                                                    variant: 'filled',
                                                    fullWidth: true,
                                                    InputLabelProps: { shrink: true },
                                                },
                                                actionBar: { actions: ['clear'] },
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: '0 0 140px', minWidth: 140 }}>
                                        <TimePicker
                                            label="Dep. to"
                                            value={getDeparturePickerValue(props.recordListFilters.departureTimeEnd)}
                                            onChange={handleDepartureTimeChange('departureTimeEnd')}
                                            ampm={true}
                                            slotProps={{
                                                textField: {
                                                    size: 'small',
                                                    variant: 'filled',
                                                    fullWidth: true,
                                                    InputLabelProps: { shrink: true },
                                                },
                                                actionBar: { actions: ['clear'] },
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: '0 0 auto', minWidth: 0, display: 'flex', alignItems: 'center', alignSelf: 'center' }}>
                                        <FormControlLabel
                                            sx={{ m: 0, alignItems: 'center' }}
                                            control={<Checkbox checked={!!props.recordListFilters.groupByMainBooking} onChange={handleGroupingToggle} />}
                                            label="Group by lead booking"
                                        />
                                    </Box>
                                </Box>
                            ) : (
                                <>
                                    <Box sx={{ width: { xs: '100%', sm: 160 }, flex: { xs: '1 1 100%', sm: '0 0 160px' } }}>
                                        <FormControl fullWidth variant="filled" size="small">
                                            <InputLabel id="override_mode">Booking state</InputLabel>
                                            <Select name="bookingState" labelId="bookingState" id="bookingState" value={props.recordListFilters.bookingState} onChange={onSelectChange} >
                                                {bookingStatesMenuItems}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 2, width: '100%', flexWrap: { xs: 'wrap', sm: 'nowrap' }, alignItems: 'center' }}>
                                        <Box sx={{ flex: { xs: '1 1 0', sm: '0 0 140px' }, minWidth: 0 }}>
                                            <TimePicker
                                                label="Dep. from"
                                                value={getDeparturePickerValue(props.recordListFilters.departureTimeStart)}
                                                onChange={handleDepartureTimeChange('departureTimeStart')}
                                                ampm={true}
                                                slotProps={{
                                                    textField: {
                                                        size: 'small',
                                                        variant: 'filled',
                                                        fullWidth: true,
                                                        InputLabelProps: { shrink: true },
                                                    },
                                                    actionBar: { actions: ['clear'] },
                                                }}
                                            />
                                        </Box>
                                        <Box sx={{ flex: { xs: '1 1 0', sm: '0 0 140px' }, minWidth: 0 }}>
                                            <TimePicker
                                                label="Dep. to"
                                                value={getDeparturePickerValue(props.recordListFilters.departureTimeEnd)}
                                                onChange={handleDepartureTimeChange('departureTimeEnd')}
                                                ampm={true}
                                                slotProps={{
                                                    textField: {
                                                        size: 'small',
                                                        variant: 'filled',
                                                        fullWidth: true,
                                                        InputLabelProps: { shrink: true },
                                                    },
                                                    actionBar: { actions: ['clear'] },
                                                }}
                                            />
                                        </Box>
                                        <Box sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' }, minWidth: 0, display: 'flex', alignItems: 'center' }}>
                                            <FormControlLabel
                                                sx={{ m: 0, alignItems: 'center' }}
                                                control={<Checkbox checked={!!props.recordListFilters.groupByMainBooking} onChange={handleGroupingToggle} />}
                                                label="Group by lead booking"
                                            />
                                        </Box>
                                    </Box>
                                </>
                            )}
                        </Box>
                    )}

                    {!accessGroupsEnabled() && (
                        <Grid item xs={12} container justifyContent="flex-end">
                            <Grid className={isSmallScreen && 'hide-scrollbar'} style={{ display: 'flex', flexWrap: 'nowrap', justifyContent: 'space-between', gap: '0.5rem', overflow: 'auto', maxWidth: '92vw' }}>
                                <Grid
                                    item
                                    xs={'auto'}
                                    style={{
                                        padding: '6px 16px',
                                        borderRadius: '16px',
                                        backgroundColor: !props.recordListFilters.roleFilter ? theme.palette.primary.main : 'white',
                                        color: !props.recordListFilters.roleFilter ? 'white' : 'rgba(0, 0, 0, 0.87)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        border: 'solid 1px lightgrey',
                                    }}
                                    key={props.recordListFilters.roleFilter ? props.recordListFilters.roleFilter?.length + 1 : null}
                                    onClick={(ev) => handleRoleFilterSelect(ev, null)}
                                >
                                    <Typography>All ({props?.numberOfTourUsers.total})</Typography>
                                </Grid>
                                {props.tourUserRoles.map((role) => {
                                    return (
                                        <Grid
                                            item
                                            xs={'auto'}
                                            style={{
                                                padding: '6px 16px',
                                                borderRadius: '16px',
                                                backgroundColor: props.recordListFilters.roleFilter?.includes(role.id) ? theme.palette.primary.main : 'white',
                                                color: props.recordListFilters.roleFilter?.includes(role.id) ? 'white' : 'rgba(0, 0, 0, 0.87)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                border: 'solid 1px lightgrey',
                                                textTransform: 'capitalize',
                                            }}
                                            key={role.id}
                                            onClick={(ev) => handleRoleFilterSelect(ev, role)}
                                        >
                                            {role.id ? (
                                                <Typography>
                                                    {role.name} ({props?.numberOfTourUsers[role.id] ? props?.numberOfTourUsers[role.id] : 0})
                                                </Typography>
                                            ) : (
                                                <Typography>Guest ({props?.numberOfTourUsers.null ? props?.numberOfTourUsers.null : 0})</Typography>
                                            )}
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Grid>
                    )}

                    {props.searchFilterList?.filter((f) => f.type !== 'role').length > 0 && !isSmallScreen && (
                        <Grid item xs={12} container gap={1}>
                            {props.searchFilterList.map((filter) => {
                                return filter.type !== 'role' ? (
                                    <Grid
                                        item
                                        className={classes.filterCapsule}
                                        key={filter.id}
                                        style={filter.exclude ? { backgroundColor: '#FDDCDC', color: '#B71C1C' } : undefined}
                                    >
                                        <Typography
                                            onClick={filter.type === 'tag' ? () => toggleExcludeSearchFilter(filter) : undefined}
                                            title={
                                                filter.type === 'tag'
                                                    ? filter.exclude
                                                        ? 'Excluding this tag — click to include'
                                                        : 'Including this tag — click to exclude'
                                                    : undefined
                                            }
                                            style={{ fontSize: '90%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, cursor: filter.type === 'tag' ? 'pointer' : 'default' }}
                                        >
                                            {filter.exclude ? '≠ ' : ''}{filter.name}
                                        </Typography>
                                        <Typography onClick={() => removeSearchFilter(filter)} style={{ fontSize: '70%', cursor: 'pointer', flexShrink: 0 }}>
                                            X
                                        </Typography>
                                    </Grid>
                                ) : null;
                            })}
                            <Grid item>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        props.setSearchFilterList((prevState) => {
                                            let existingRoleSearchFilters = prevState.filter((f) => f.type === 'role');
                                            return [...existingRoleSearchFilters];
                                        });
                                    }}
                                >
                                    Clear Filters
                                </Button>
                            </Grid>
                        </Grid>
                    )}
                </Box>
            </Toolbar>
        </LocalizationProvider >
    );
};

UserListToolbar.propTypes = {
    trip: PropTypes.shape({
        id: PropTypes.number,
        is_bespoke: PropTypes.bool,
        schedule: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
        tag_categories: PropTypes.arrayOf(PropTypes.object),
    }).isRequired,
    recordListFilters: PropTypes.shape({
        dateStartFilter: PropTypes.string,
        dateEndFilter: PropTypes.string,
        departureTimeStart: PropTypes.string,
        departureTimeEnd: PropTypes.string,
        roleFilter: PropTypes.arrayOf(PropTypes.any),
        bookingState: PropTypes.any,
        groupByMainBooking: PropTypes.bool,
        agroups: PropTypes.arrayOf(PropTypes.object),
    }).isRequired,
    setRecordListFilters: PropTypes.func.isRequired,
    setLastUpdate: PropTypes.func.isRequired,
    searchFilterList: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.any.isRequired,
        name: PropTypes.string.isRequired,
        type: PropTypes.string,
    })).isRequired,
    setSearchFilterList: PropTypes.func.isRequired,
    selectedIds: PropTypes.array,
    syncActive: PropTypes.bool,
    deleteUsers: PropTypes.func,
    addTags: PropTypes.func,
    nonRequiredTripAssociatedTagsIds: PropTypes.array,
    removeTags: PropTypes.func,
    addObservedUsers: PropTypes.func,
    removeObservedUsers: PropTypes.func,
    openBatchChangeOwner: PropTypes.func,
    tourUserId: PropTypes.any,
    showUserAddForm: PropTypes.func,
    id: PropTypes.any,
    setRecordListQuery: PropTypes.func,
    numberOfTourUsers: PropTypes.object,
    tourUserRoles: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.any,
        name: PropTypes.string.isRequired,
    })),
    addUserFlowOpen: PropTypes.bool,
    onExportList: PropTypes.func,
    onDocumentExport: PropTypes.func,
    onSelectAll: PropTypes.func,
    onDeselectAll: PropTypes.func,
};

UserListToolbar.defaultProps = {
    selectedIds: [],
    nonRequiredTripAssociatedTagsIds: [],
    syncActive: false,
    tourUserRoles: [],
    searchFilterList: [],
    addUserFlowOpen: false,
};

export default UserListToolbar;
